import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, MenuItem, TextField, Button, CircularProgress,
  Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Divider, IconButton, Tooltip, Collapse,
  Badge, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add, ArrowUpward, ArrowDownward, Delete, CheckCircle, Cancel, Warning,
  WifiOff, Sync, Storage,
} from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../../api/apiClient';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const BG        = '#f4f7f5';
const SURFACE   = '#ffffff';
const BORDER    = '#d6e4de';
const ACCENT    = '#1e6b45';
const ACCENT_BG = '#e6f2ec';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
const WARN_BG   = '#fffbeb';
const WARN_BOR  = '#f59e0b';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: SURFACE, color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(30,107,69,0.10)',
      '& .MuiMenuItem-root': {
        color: TEXT_PRI,
        '&:hover': { bgcolor: ACCENT_BG },
        '&.Mui-selected': { bgcolor: ACCENT_BG, color: ACCENT },
        '&.Mui-selected:hover': { bgcolor: ACCENT_BG },
      },
    },
  },
};

const thCell = (label, align = 'left') => (
  <TableCell key={label} align={align} sx={{
    color: TEXT_SEC, fontWeight: 700, fontSize: '0.68rem',
    textTransform: 'uppercase', letterSpacing: '0.08em',
    borderBottom: `1px solid ${BORDER}`, py: 1.5, bgcolor: BG,
  }}>{label}</TableCell>
);

// ─── IndexedDB helpers ────────────────────────────────────────────────────────
const DB_NAME    = 'offlineChangesDB';
const DB_VERSION = 1;
const STORE      = 'pendingChanges';

const openDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => e.target.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });

const savePendingChange = async (gameId, change) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add({ gameId, change, createdAt: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = (e) => reject(e.target.error);
  });
};

const getPendingChangesByGame = async (gameId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx      = db.transaction(STORE, 'readonly');
    const results = [];
    tx.objectStore(STORE).openCursor().onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) { if (cursor.value.gameId === gameId) results.push(cursor.value); cursor.continue(); }
      else resolve(results);
    };
    tx.onerror = (e) => reject(e.target.error);
  });
};

const deletePendingChangesByGame = async (gameId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.openCursor().onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) { if (cursor.value.gameId === gameId) cursor.delete(); cursor.continue(); }
      else resolve();
    };
    tx.onerror = (e) => reject(e.target.error);
  });
};

const deletePendingChangeById = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = (e) => reject(e.target.error);
  });
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isOnline = () => navigator.onLine;

const getUniquePlayers = (players = []) => {
  const map = new Map();
  players.forEach(({ entityId, userIds }) => {
    (userIds || []).forEach((u) => { if (!map.has(u._id)) map.set(u._id, { ...u, entityId }); });
  });
  return [...map.values()];
};

const playerLabel = (p) =>
  `${p.firstName} ${p.lastName}${p.actualJerseyNumber ? ` #${p.actualJerseyNumber}` : ''}`;

// ─── Offline Changes Dialog ───────────────────────────────────────────────────
const OfflineChangesDialog = ({
  open, onClose, pendingRows, allPlayers, entities,
  onDelete, onDeleteAll, syncing, onSync, online,
}) => {
  const findPlayer = (id) => allPlayers.find((p) => p._id === id);
  const findEntity = (id) => entities.find((e) => e._id === id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', bgcolor: SURFACE } }}>
      <DialogTitle sx={{ color: TEXT_PRI, fontWeight: 700, pb: 1 }}>
        Remplacements en attente ({pendingRows.length})
      </DialogTitle>

      <DialogContent dividers>
        {pendingRows.length === 0 ? (
          <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucun remplacement en attente.</Typography>
        ) : (
          <List dense disablePadding>
            {pendingRows.map((row) => {
              const c      = row.change;
              const entity = findEntity(c.entityId);
              const pOut   = findPlayer(c.out);
              const pIn    = findPlayer(c.in);
              return (
                <ListItem key={row.id} divider
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => onDelete(row.id)}
                      sx={{ color: '#c62828', bgcolor: '#fdecea', borderRadius: '6px', '&:hover': { bgcolor: '#f9cbc8' } }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                  sx={{ py: 1, '&:hover': { bgcolor: ACCENT_BG }, borderRadius: '8px' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: ACCENT }}>
                          {c.minute}'
                        </Typography>
                        <Typography variant="body2" sx={{ color: TEXT_SEC }}>
                          {entity?.name || c.entityId}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowDownward fontSize="small" color="error" />
                          <Typography variant="caption" sx={{ color: TEXT_PRI }}>
                            {pOut ? playerLabel(pOut) : c.out}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowUpward fontSize="small" color="success" />
                          <Typography variant="caption" sx={{ color: TEXT_PRI }}>
                            {pIn ? playerLabel(pIn) : c.in}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {pendingRows.length > 0 && (
          <Button size="small" color="error" variant="outlined" onClick={onDeleteAll}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}>
            Tout supprimer
          </Button>
        )}
        {online && pendingRows.length > 0 && (
          <Button size="small" variant="contained" onClick={onSync} disabled={syncing}
            startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <Sync fontSize="small" />}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '8px',
              bgcolor: ACCENT, color: '#fff', '&:hover': { bgcolor: '#175c39' },
            }}>
            Synchroniser
          </Button>
        )}
        <Button size="small" onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: '8px', color: TEXT_SEC }}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ChangesTab = ({ game, onRefresh }) => {
  const [form, setForm]             = useState({ entityId: '', out: '', in: '', minute: '' });
  const [loading, setLoading]       = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId]   = useState(null);
  const [error, setError]           = useState('');
  const [snack, setSnack]           = useState({ open: false, message: '', severity: 'success' });
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingRows, setPendingRows]   = useState([]);
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [online, setOnline]             = useState(isOnline());
  const [canAddSubstitution, setCanAddSubstitution] = useState(false);

  const syncLockRef = useRef(false);

  // ── Refresh pending list ──
  const refreshPending = useCallback(async () => {
    try {
      const rows = await getPendingChangesByGame(game._id);
      setPendingRows(rows);
      setPendingCount(rows.length);
    } catch {}
  }, [game._id]);

  // ── Sync to server ──
  const syncPendingChanges = useCallback(async () => {
    if (syncLockRef.current || !isOnline()) return;
    syncLockRef.current = true;
    setSyncing(true);
    try {
      const pending = await getPendingChangesByGame(game._id);
      if (pending.length === 0) { setSyncing(false); syncLockRef.current = false; return; }

      await apiCall((headers) =>
        axios.post(
          `${BASE_URL}/games/${game._id}/changes/bulk`,
          { changes: pending.map((r) => r.change) },
          { headers }
        )
      );

      await deletePendingChangesByGame(game._id);
      setPendingRows([]);
      setPendingCount(0);
      setDialogOpen(false);
      setSnack({ open: true, message: `${pending.length} remplacement(s) synchronisé(s) avec succès !`, severity: 'success' });
      onRefresh();
    } catch {
      setSnack({ open: true, message: 'Échec de la synchronisation, réessai ultérieur…', severity: 'warning' });
    } finally {
      setSyncing(false);
      syncLockRef.current = false;
    }
  }, [game._id, onRefresh]);

  // ── Delete single pending ──
  const handleDeletePending = useCallback(async (id) => {
    try { await deletePendingChangeById(id); await refreshPending(); } catch {}
  }, [refreshPending]);

  // ── Delete all pending ──
  const handleDeleteAllPending = useCallback(async () => {
    try {
      await deletePendingChangesByGame(game._id);
      setPendingRows([]); setPendingCount(0); setDialogOpen(false);
    } catch {}
  }, [game._id]);

  // ── Open dialog ──
  const handleOpenDialog = useCallback(async () => {
    await refreshPending(); setDialogOpen(true);
  }, [refreshPending]);

  // ── Online/offline listeners ──
  useEffect(() => {
    const handleOnline  = () => { setOnline(true);  syncPendingChanges(); };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshPending();
    if (isOnline()) syncPendingChanges();
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingChanges, refreshPending]);

  // // ── Permission check ──
  // useEffect(() => {
  //   const currentUser = (() => {
  //     try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  //   })();
  //   if (!currentUser) return;
  //   if (currentUser.role === 'entityPlatformAdmin') { setCanAddSubstitution(true); return; }
  //   const ownerEntry = (game?.eventOwnersAndRoles || []).find((e) => e.userId?._id === currentUser._id);
  //   if (!ownerEntry) return;
  //   const roles = ownerEntry.roles || [];
  //   setCanAddSubstitution(roles.includes('eventSuperAdmin') || roles.includes('eventGameUpdater'));
  // }, [game?._id]);


  // ── Permission check ──
useEffect(() => {
  const currentUser = (() => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; } catch { return null; }
  })();
  if (!currentUser) return;
  const ownerEntry = (game?.eventOwnersAndRoles || []).find((e) => e.userId?._id === currentUser._id);
  if (!ownerEntry) return;
  setCanAddSubstitution(true);
}, [game?._id]);

  // ── Derived data ──
  const entities = useMemo(() => {
    const map = new Map();
    (game?.players || []).forEach(({ entityId }) => { if (entityId?._id) map.set(entityId._id, entityId); });
    return [...map.values()];
  }, [game]);

  const allPlayers = useMemo(() => getUniquePlayers(game?.players || []), [game]);

  const playersForEntity = useMemo(() => {
    if (!form.entityId) return [];
    return allPlayers.filter((p) => p.entityId?._id === form.entityId);
  }, [form.entityId, allPlayers]);

  const handleChange       = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const handleEntityChange = (e) => setForm((p) => ({ ...p, entityId: e.target.value, out: '', in: '' }));

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.entityId || !form.out || !form.in || !form.minute) {
      setError('Équipe, Joueur sortant, Joueur entrant et Minute sont obligatoires'); return;
    }
    if (form.out === form.in) { setError('Le joueur sortant et le joueur entrant doivent être différents'); return; }
    setLoading(true); setError('');

    const changePayload = {
      entityId: form.entityId,
      out:      form.out,
      in:       form.in,
      minute:   Number(form.minute),
    };

    if (isOnline()) {
      try {
        await apiCall((headers) =>
          axios.post(`${BASE_URL}/games/${game._id}/changes`, changePayload, { headers })
        );
        setSnack({ open: true, message: 'Remplacement ajouté avec succès !', severity: 'success' });
        setForm({ entityId: '', out: '', in: '', minute: '' });
        onRefresh();
      } catch (err) {
        setError(err.response?.data?.message || 'Échec de l\'ajout du remplacement');
      }
    } else {
      try {
        await savePendingChange(game._id, changePayload);
        await refreshPending();
        setSnack({
          open: true,
          message: 'Hors-ligne — remplacement sauvegardé localement, sera synchronisé à la reconnexion.',
          severity: 'warning',
        });
        setForm({ entityId: '', out: '', in: '', minute: '' });
      } catch {
        setError('Impossible de sauvegarder le remplacement localement');
      }
    }
    setLoading(false);
  };

  const handleDeleteRequest = (id) => { setConfirmId(id); setError(''); };
  const handleDeleteCancel  = () => setConfirmId(null);
  const handleDeleteConfirm = async () => {
    const changeId = confirmId;
    setConfirmId(null); setDeletingId(changeId); setError('');
    try {
      await apiCall((headers) =>
        axios.delete(`${BASE_URL}/games/${game._id}/changes/${changeId}`, { headers })
      );
      setDeletingId(null);
      setSnack({ open: true, message: 'Remplacement supprimé avec succès !', severity: 'success' });
      onRefresh();
    } catch (err) {
      setDeletingId(null);
      setError(err.response?.data?.message || 'Échec de la suppression du remplacement');
    }
  };

  return (
    <Box sx={{ color: TEXT_PRI }}>

      {/* ── Offline / pending banner ── */}
      {(!online || pendingCount > 0) && (
        <Alert
          severity={online ? 'info' : 'warning'}
          icon={online ? <Sync fontSize="small" /> : <WifiOff fontSize="small" />}
          sx={{ mb: 1.5, borderRadius: '10px' }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {pendingCount > 0 && (
                <Badge badgeContent={pendingCount} color="warning" overlap="rectangular">
                  <Button
                    size="small" variant="outlined"
                    onClick={handleOpenDialog}
                    startIcon={<Storage fontSize="small" />}
                    sx={{
                      textTransform: 'none', fontWeight: 600,
                      borderColor: WARN_BOR, color: '#92400e', borderRadius: '7px',
                      '&:hover': { bgcolor: WARN_BG, borderColor: '#f59e0b' },
                    }}>
                    Voir
                  </Button>
                </Badge>
              )}
              {online && pendingCount > 0 && (
                <Button
                  size="small" onClick={syncPendingChanges} disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <Sync fontSize="small" />}
                  sx={{ textTransform: 'none', color: ACCENT, fontWeight: 600 }}>
                  Synchroniser
                </Button>
              )}
            </Box>
          }>
          {!online && pendingCount === 0 && 'Hors-ligne — les remplacements seront sauvegardés localement.'}
          {!online && pendingCount > 0  && `Hors-ligne — ${pendingCount} remplacement(s) en attente de synchronisation.`}
          {online  && pendingCount > 0  && `${pendingCount} remplacement(s) en attente de synchronisation.`}
        </Alert>
      )}

      {/* ── Offline Changes Dialog ── */}
      <OfflineChangesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        pendingRows={pendingRows}
        allPlayers={allPlayers}
        entities={entities}
        onDelete={handleDeletePending}
        onDeleteAll={handleDeleteAllPending}
        syncing={syncing}
        onSync={syncPendingChanges}
        online={online}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{
            borderRadius: '10px',
            ...(snack.severity === 'success' && {
              bgcolor: ACCENT_BG, color: ACCENT,
              border: `1px solid ${ACCENT}55`, fontWeight: 600,
              '& .MuiAlert-icon': { color: ACCENT },
              '& .MuiIconButton-root': { color: ACCENT },
            }),
          }}>
          {snack.message}
        </Alert>
      </Snackbar>

      {/* ── Form ── */}
      {canAddSubstitution && (
        <>
          <Typography variant="subtitle2" gutterBottom sx={{ color: TEXT_PRI, fontWeight: 700 }}>
            Ajouter un remplacement
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 1, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Équipe" value={form.entityId}
                onChange={handleEntityChange} sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {entities.map((e) => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Minute" type="number" value={form.minute}
                onChange={handleChange('minute')} inputProps={{ min: 1, max: 120 }} sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Joueur sortant" value={form.out}
                onChange={handleChange('out')} disabled={!form.entityId} sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {playersForEntity.map((p) => <MenuItem key={p._id} value={p._id}>{playerLabel(p)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Joueur entrant" value={form.in}
                onChange={handleChange('in')} disabled={!form.entityId} sx={inputSx} SelectProps={{ MenuProps: menuProps }}>
                {playersForEntity.map((p) => <MenuItem key={p._id} value={p._id}>{playerLabel(p)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" size="small" onClick={handleSubmit} disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Add />}
                sx={{
                  bgcolor: ACCENT, color: '#ffffff', borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(30,107,69,0.25)',
                  '&:hover': { bgcolor: '#175c39', boxShadow: '0 6px 16px rgba(30,107,69,0.35)' },
                  '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
                }}>
                Ajouter un remplacement
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 1.5, borderColor: BORDER }} />
        </>
      )}

      {/* ── Table ── */}
      <Typography variant="subtitle2" gutterBottom sx={{ color: TEXT_PRI, fontWeight: 700 }}>
        Remplacements ({game?.change?.length || 0})
      </Typography>

      {game?.change?.length > 0 ? (
        <TableContainer component={Paper} elevation={0}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', bgcolor: SURFACE, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {thCell('Équipe')}
                {thCell('Joueur sortant')}
                {thCell('Joueur entrant')}
                {thCell('Minute')}
                {canAddSubstitution && thCell('Action', 'center')}
              </TableRow>
            </TableHead>
            <TableBody>
              {game.change.map((c, i) => {
                const isConfirming = confirmId  === c._id;
                const isDeleting   = deletingId === c._id;
                return (
                  <React.Fragment key={c._id || i}>
                    <TableRow hover sx={{
                      '&:hover': { bgcolor: ACCENT_BG }, '& td': { borderColor: BORDER },
                      ...(isConfirming && { bgcolor: '#fff5f5', opacity: 0.9 }),
                    }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                          {(() => {
                            if (c.entityId?.name) return c.entityId.name;
                            const outId = c.out?._id; const inId = c.in?._id;
                            for (const entry of game?.players || []) {
                              const ids = (entry.userIds || []).map(u => u._id);
                              if (ids.includes(outId) || ids.includes(inId)) return entry.entityId?.name || '—';
                            }
                            return '—';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowDownward fontSize="small" color="error" />
                          <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                            {c.out?.firstName} {c.out?.lastName}
                            {c.out?.actualJerseyNumber ? ` #${c.out.actualJerseyNumber}` : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowUpward fontSize="small" color="success" />
                          <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                            {c.in?.firstName} {c.in?.lastName}
                            {c.in?.actualJerseyNumber ? ` #${c.in.actualJerseyNumber}` : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: ACCENT }}>{c.minute}'</Typography>
                      </TableCell>
                      {canAddSubstitution && (
                        <TableCell align="center">
                          {isDeleting ? (
                            <CircularProgress size={16} sx={{ color: ACCENT }} />
                          ) : isConfirming ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Confirmer">
                                <IconButton size="small" color="error" onClick={handleDeleteConfirm}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Annuler">
                                <IconButton size="small" onClick={handleDeleteCancel} sx={{ color: TEXT_SEC }}>
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Tooltip title="Supprimer">
                              <IconButton size="small" onClick={() => handleDeleteRequest(c._id)}
                                sx={{ color: '#c62828', bgcolor: '#fdecea', borderRadius: '6px', '&:hover': { bgcolor: '#f9cbc8' } }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>

                    {canAddSubstitution && (
                      <TableRow sx={{ p: 0 }}>
                        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                          <Collapse in={isConfirming} unmountOnExit>
                            <Alert severity="warning" icon={<Warning fontSize="small" />}
                              sx={{
                                borderRadius: 0, py: 0.5, px: 2, bgcolor: '#fff8e1',
                                '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1, width: '100%' },
                              }}
                              action={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button size="small" variant="contained" color="error"
                                    onClick={handleDeleteConfirm}
                                    startIcon={<Delete fontSize="small" />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#ffffff' }}>
                                    Supprimer
                                  </Button>
                                  <Button size="small" variant="outlined" onClick={handleDeleteCancel}
                                    sx={{ textTransform: 'none', borderRadius: '8px', color: TEXT_SEC, borderColor: BORDER }}>
                                    Annuler
                                  </Button>
                                </Box>
                              }>
                              <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                                Confirmer la suppression de ce remplacement ?
                              </Typography>
                            </Alert>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" sx={{ color: TEXT_SEC }}>Aucun remplacement pour l'instant</Typography>
      )}
    </Box>
  );
};

export default ChangesTab;